import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getAiSettings, saveAiSettings, testAiSettings } from "../api/aiSettingsApi";

const card = { display: "grid", gap: 14, maxWidth: 640, padding: 20, border: "1px solid var(--border)", borderRadius: 8 };
const field = { display: "grid", gap: 4, fontSize: 13, color: "var(--text-muted)" };
const input = { padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--bg-card)", color: "var(--text-primary)" };
const btn = { padding: "8px 16px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text-primary)", cursor: "pointer" };

// Superadmin only (menu + server). Where the AI features (incident diagnosis, RCA, fleet query) send their requests.
export default function AiSettings() {
  const { t } = useTranslation();
  const accessToken = useSelector((s) => s.session?.accessToken);
  const superadmin = useSelector((s) => s.session?.me?.global_role) === "superadmin";
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState({ enabled: false, provider: "anthropic", base_url: "", model: "", api_key: "" });
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    if (!accessToken || !superadmin) return;
    try {
      const s = await getAiSettings(accessToken);
      setSaved(s);
      setForm({ enabled: s.enabled, provider: s.provider, base_url: s.base_url || "", model: s.model || "", api_key: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || t("admin.ai.loadFailed"));
    }
  }, [accessToken, superadmin, t]);

  useEffect(() => { load(); }, [load]);

  if (!superadmin) return <div style={{ padding: 24, color: "var(--text-muted)" }}>{t("admin.ai.forbidden")}</div>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const s = await saveAiSettings(accessToken, { ...form, api_key: form.api_key || null });
      setSaved(s);
      setForm({ ...form, api_key: "" });
      toast.success(t("admin.ai.saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("admin.ai.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    try {
      setTestResult(await testAiSettings(accessToken));
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || t("admin.ai.testFailed") });
    } finally {
      setBusy(false);
    }
  };

  const compat = form.provider === "openai_compatible";
  const providers = [
    { value: "anthropic", label: "Anthropic (Claude)" },
    { value: "openai_compatible", label: t("admin.ai.providerOpenai") },
  ];
  return (
    <div style={{ padding: 24, color: "var(--text-primary)" }}>
      <h2 style={{ marginTop: 0 }}>{t("admin.ai.title")}</h2>
      <p style={{ color: "var(--text-muted)", maxWidth: 640, fontSize: 13 }}>
        {t("admin.ai.intro")}
      </p>
      <div style={card}>
        <label style={{ ...field, display: "flex", gap: 8, alignItems: "center", color: "var(--text-primary)" }}>
          <input type="checkbox" checked={form.enabled} onChange={set("enabled")} /> {t("admin.ai.enabled")}
        </label>
        <label style={field}>
          {t("admin.ai.provider")}
          <select style={input} value={form.provider} onChange={set("provider")}>
            {providers.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
        <label style={field}>
          {compat ? t("admin.ai.urlRequired") : t("admin.ai.urlOptional")}
          <input style={input} value={form.base_url} onChange={set("base_url")} placeholder={compat ? "https://.../v1" : t("admin.ai.urlPlaceholderEmpty")} />
        </label>
        <label style={field}>
          {compat ? t("admin.ai.modelRequired") : t("admin.ai.modelOptional", { model: saved?.default_model || t("admin.ai.defaultModel") })}
          <input style={input} value={form.model} onChange={set("model")} />
        </label>
        <label style={field}>
          {saved?.has_key ? t("admin.ai.apiKeySaved", { hint: saved.key_hint || "••••" }) : t("admin.ai.apiKeyNone")}
          <input style={input} type="password" autoComplete="new-password" value={form.api_key} onChange={set("api_key")} />
        </label>
        <div style={{ display: "flex", gap: 10 }}>
          <button style={btn} disabled={busy} onClick={save}>{t("admin.ai.save")}</button>
          <button style={btn} disabled={busy || !saved?.has_key} onClick={test}>{t("admin.ai.test")}</button>
        </div>
        {testResult && (
          <div style={{ fontSize: 13, color: testResult.ok ? "var(--success)" : "var(--danger, #e53e3e)" }}>
            {testResult.ok ? t("admin.ai.testOk", { model: testResult.model, reply: testResult.reply }) : t("admin.ai.testError", { error: testResult.error })}
          </div>
        )}
      </div>
    </div>
  );
}
